import { Search, Settings, Bell, MessageSquare, Activity, ArrowLeft, Settings2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material'
import Navbar from "@/layout/Navbar"
type Notification = {
    id: number
    type: "all" | "support" | "reading" | "message"
    title: string
    description: string
    time: string
}

const notifications: Notification[] = [
    {
        id: 1,
        type: "reading",
        title: "Your Daily Reading is Ready",
        description: "Your Three Card Spread reading for today is now available",
        time: "5m ago",
    },
    {
        id: 2,
        type: "message",
        title: "New message from your Tarot Advisor",
        description: "I've reviewed your recent reading and have some additional insights to share",
        time: "1h ago",
    },
    {
        id: 3,
        type: "support",
        title: "Reading Session Confirmed",
        description: "Your live reading session with Sarah is scheduled for tomorrow at 2 PM",
        time: "2h ago"
    },
    {
        id: 4,
        type: "reading",
        title: "Weekly Forecast Complete",
        description: "Your weekly tarot forecast has been generated. Check it out!",
        time: "1d ago",
    },
    {
        id: 5,
        type: "message",
        title: "Community Discussion",
        description: "New responses in 'Understanding The Tower Card' thread",
        time: "1d ago",
    },
    {
        id: 6,
        type: "support",
        title: "Subscription Renewed",
        description: "Your premium tarot subscription has been successfully renewed",
        time: "2d ago",
    },
    {
        id: 7,
        type: "reading",
        title: "New Moon Reading Available",
        description: "Special lunar phase reading is now ready for your review",
        time: "2d ago",
    },
    {
        id: 8,
        type: "message",
        title: "Group Reading Invitation",
        description: "You've been invited to join a group reading session this weekend",
        time: "3d ago",
    },
    {
        id: 9,
        type: "support",
        title: "Account Milestone",
        description: "You've completed 50 readings! Unlock your achievement badge",
        time: "4d ago",
    },
    {
        id: 10,
        type: "reading",
        title: "Celtic Cross Spread Complete",
        description: "Your in-depth Celtic Cross reading results are ready",
        time: "5d ago",
    },
]

export default function NotificationsPage() {
    const navigate = useNavigate()
    const { t } = useTranslation();
    const onBackClick = () => {
        navigate(-1)
    }
    const [activeTab, setActiveTab] = useState("All")
    const TABS = [t('notifications.tabs.all'), t('notifications.tabs.support'), t('notifications.tabs.reading'), t('notifications.tabs.message')]
    const isVisible = (notification: Notification) => {

        return activeTab === t('notifications.tabs.all') || String(activeTab).toLowerCase() === notification.type
    }

    return (
        <Box
            className="min-h-screen"
            sx={{
                background: 'linear-gradient(135deg, #F8F1EE 0%, #F5F3F0 100%)',
                minHeight: '100vh',
            }}
        >
            <header className="bg-white/70 shadow-sm sticky top-0 z-50 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <button
                        onClick={onBackClick}
                        className="p-2 -ml-2 mr-3 rounded-full text-[#2B2E4A] hover:bg-white/50 active:bg-white/60 transition-colors"
                        aria-label="Go back"
                    >
                        <ArrowLeft className="h-6 w-6" />
                    </button>
                    <div className="relative flex-grow max-w-sm">
                        <Input
                            type="text"
                            placeholder={t('notifications.searchMessages')}
                            className="pl-10 pr-4 py-2 w-full bg-white/70 border-[#C9ADA7]/40 focus:border-[#2B2E4A] focus:ring-[#2B2E4A]"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#2B2E4A]/60 h-5 w-5" />
                    </div>
                    <button
                        className="ml-4 p-2 rounded-full hover:bg-white/50 text-[#2B2E4A]"
                        onClick={() => navigate("/settings")}
                    >
                        <Settings2 className="h-6 w-6" />
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">


                <div className="flex items-center gap-3 overflow-x-auto pb-2 mb-6 p-1 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-1.5 rounded-full shadow-sm
                            transition-all duration-200 ease-in-out
                            text-sm font-medium
                            border border-[#C9ADA7]/40
                            hover:shadow-md
                            focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#2B2E4A]
                            active:scale-95
                            ${activeTab === tab
                                    ? 'bg-[#2B2E4A] text-white hover:bg-[#3D4066]'
                                    : 'bg-white/70 text-[#2B2E4A] hover:bg-white'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <ul className="space-y-4">
                    {notifications.map((notification) => {
                        if (isVisible(notification))
                            return (
                                <li
                                    key={notification.id}
                                    className="bg-white/70 backdrop-blur-sm shadow rounded-xl p-4 hover:shadow-md transition-all duration-200 hover:transform hover:-translate-y-0.5 border border-[#C9ADA7]/40"
                                >
                                    <button className="w-full text-left flex items-start space-x-4">
                                        <span className="flex-shrink-0">
                                            {notification.type === "support" && <Bell className="h-6 w-6 text-[#2B2E4A]" />}
                                            {notification.type === "reading" && <Activity className="h-6 w-6 text-[#2B2E4A]" />}
                                            {notification.type === "message" && <MessageSquare className="h-6 w-6 text-[#2B2E4A]" />}
                                            {notification.type === "all" && <Bell className="h-6 w-6 text-[#2B2E4A]" />}
                                        </span>
                                        <div className="flex-grow">
                                            <h3 className="text-sm font-medium text-[#2B2E4A] font-['Playfair_Display']">{notification.title}</h3>
                                            <p className="mt-1 text-sm text-[#2B2E4A]/70 font-['Plus_Jakarta_Sans']">{notification.description}</p>
                                            <span className="mt-2 text-xs text-[#2B2E4A]/50">{notification.time}</span>
                                        </div>
                                    </button>
                                </li>
                            )
                    })}
                </ul>
            </main>
            <Navbar />
        </Box>
    )
}
